// The drop-in touch layer's DOM part (suggestion "drop-in touch layer"; feel recipe §18): a full-screen overlay with a
// floating stick in the lower left, drag to look on the right, and a button cluster laid out from data
// (`layoutTouch`). It writes the same `Controls` table as every other device ("Touch:<name>" controls), so the action
// maps, contexts, buffer and hold modes apply unchanged and gameplay never forks on touch.
//
// Each pointer is owned by the control it landed on (pointer capture, so a thumb sliding off a held button keeps it
// held); a press acts on pointer-down; everything is released on blur, pagehide, a hidden page, orientation change
// and when the overlay hides. The overlay's pointer events don't bubble to the page, so the mouse wiring
// (`attachDom`) never sees them. Safe areas come from `env(safe-area-inset-*)` (the page needs
// `viewport-fit=cover`). Colours are CSS variables: --rpg-touch-fg, --rpg-touch-bg, --rpg-touch-down.
import type { Controls } from "../input/controls";
import { layoutTouch, stickMove, type Insets, type PlacedButton, type StickState, type TouchButtonSpec, type TouchLayout, type TouchLayoutSpec } from "../input/touch-layout";

export interface TouchOverlayOptions {
  /** Where the overlay writes. */
  controls: Pick<Controls, "press" | "release" | "setVector" | "addDelta"> & Partial<Pick<Controls, "noteDevice">>;
  /** The layout data. */
  spec: TouchLayoutSpec;
  /** Where the overlay goes (default `document.body`). */
  parent?: HTMLElement;
  /** A button's text (the game's strings table); default none. */
  label?: (b: TouchButtonSpec) => string;
  /** Safe-area insets, px (default: read from `env(safe-area-inset-*)`). */
  insets?: () => Insets;
}

/** What `state()` returns: enough for a test to assert on without reading the DOM. */
export interface TouchOverlayState {
  shown: boolean;
  width: number;
  height: number;
  insets: Insets;
  stick: { active: boolean; cx: number; cy: number; x: number; y: number; mag: number; run: boolean; restX: number; restY: number; radius: number };
  look: { active: boolean };
  buttons: (PlacedButton & { down: boolean; shown: boolean })[];
}

export interface TouchOverlay {
  readonly element: HTMLElement;
  /** Show or hide the whole overlay (hiding releases everything). */
  show(on: boolean): void;
  readonly shown: boolean;
  /** Show or hide one button that the data hides by default (or hide a shown one). */
  reveal(id: string, on: boolean): void;
  /** The current layout. */
  layout(): TouchLayout;
  /** Lay out again now (resize and orientation changes do it on the next frame). */
  relayout(): void;
  state(): TouchOverlayState;
  /** Release every held control. */
  releaseAll(): void;
  destroy(): void;
}

const CSS = `
.rpg-touch{position:fixed;inset:0;z-index:10;touch-action:none;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;display:none;
  --rpg-touch-fg:rgba(255,255,255,.92);--rpg-touch-bg:rgba(20,28,24,.32);--rpg-touch-down:rgba(255,255,255,.38)}
.rpg-touch-ring{position:absolute;border-radius:50%;border:2px solid var(--rpg-touch-fg);background:var(--rpg-touch-bg);opacity:.55;pointer-events:none}
.rpg-touch-ring[data-active]{opacity:.9}
.rpg-touch-knob{position:absolute;left:50%;top:50%;width:44%;height:44%;margin:-22% 0 0 -22%;border-radius:50%;background:var(--rpg-touch-fg);opacity:.8}
.rpg-touch-btn{position:absolute;box-sizing:border-box;border-radius:50%;border:2px solid var(--rpg-touch-fg);background:var(--rpg-touch-bg);
  color:var(--rpg-touch-fg);display:flex;align-items:center;justify-content:center;text-align:center;font:600 13px/1.1 system-ui,sans-serif;
  text-shadow:0 1px 2px rgba(0,0,0,.5)}
.rpg-touch-btn[data-size=primary]{font-size:15px}
.rpg-touch-btn[data-size=small]{font-size:11px}
.rpg-touch-btn[data-down]{background:var(--rpg-touch-down)}
.rpg-touch-btn[data-hidden]{display:none}
`;

type Owner = { kind: "stick" } | { kind: "look"; x: number; y: number } | { kind: "button"; id: string; control: string };

/** Mount the overlay (hidden until `show(true)`). */
export function createTouchOverlay(o: TouchOverlayOptions): TouchOverlay {
  const doc = (o.parent ?? document.body).ownerDocument;
  const win = doc.defaultView!;
  const parent = o.parent ?? doc.body;
  if (!doc.getElementById("rpg-touch-css")) {
    const style = doc.createElement("style");
    style.id = "rpg-touch-css";
    style.textContent = CSS;
    doc.head.appendChild(style);
  }
  const root = doc.createElement("div");
  root.className = "rpg-touch";
  root.dataset.touchOverlay = "";
  const ring = doc.createElement("div");
  ring.className = "rpg-touch-ring";
  const knob = doc.createElement("div");
  knob.className = "rpg-touch-knob";
  ring.appendChild(knob);
  root.appendChild(ring);
  const els = new Map<string, HTMLElement>();
  for (const b of o.spec.buttons) {
    const el = doc.createElement("div");
    el.className = "rpg-touch-btn";
    el.dataset.touchButton = b.id;
    el.dataset.size = b.size;
    el.textContent = o.label?.(b) ?? "";
    root.appendChild(el);
    els.set(b.id, el);
  }
  parent.appendChild(root);

  const probe = doc.createElement("div");
  probe.style.cssText = "position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)";
  parent.appendChild(probe);
  const readInsets = (): Insets => {
    const cs = win.getComputedStyle(probe);
    const n = (v: string): number => Number.parseFloat(v) || 0;
    return { top: n(cs.paddingTop), right: n(cs.paddingRight), bottom: n(cs.paddingBottom), left: n(cs.paddingLeft) };
  };

  let shown = false;
  const revealed = new Map<string, boolean>();
  let lay: TouchLayout = layoutTouch(o.spec, win.innerWidth, win.innerHeight);
  const owners = new Map<number, Owner>();
  let stickOwner: number | null = null;
  let stick: StickState & { x: number; y: number; mag: number } = { cx: 0, cy: 0, run: false, x: 0, y: 0, mag: 0 };
  const down = new Set<string>();
  /** The thumb's offset from the ring's centre (never past the rim: the ring slides). */
  let knob_ = { x: 0, y: 0 };

  const isHidden = (b: PlacedButton): boolean => revealed.get(b.id) ?? b.hidden;
  const drawStick = (): void => {
    const R = lay.stick.radius;
    const cx = stickOwner === null ? lay.stick.restX : stick.cx;
    const cy = stickOwner === null ? lay.stick.restY : stick.cy;
    ring.style.cssText = `left:${cx - R}px;top:${cy - R}px;width:${2 * R}px;height:${2 * R}px`;
    if (stickOwner === null) ring.removeAttribute("data-active");
    else ring.dataset.active = "";
    knob.style.transform = stickOwner === null ? "" : `translate(${knob_.x}px, ${knob_.y}px)`;
  };
  const relayout = (): void => {
    lay = layoutTouch(o.spec, win.innerWidth, win.innerHeight, o.insets?.() ?? readInsets());
    for (const b of lay.buttons) {
      const el = els.get(b.id)!;
      el.style.left = `${b.x - b.size / 2}px`;
      el.style.top = `${b.y - b.size / 2}px`;
      el.style.width = el.style.height = `${b.size}px`;
      if (isHidden(b)) el.dataset.hidden = "";
      else el.removeAttribute("data-hidden");
    }
    drawStick();
  };

  const capture = (el: Element, id: number): void => {
    try {
      el.setPointerCapture(id);
    } catch {
      // (synthetic events have no active pointer to capture)
    }
  };
  const inRect = (x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

  const end = (id: number): void => {
    const own = owners.get(id);
    if (!own) return;
    owners.delete(id);
    if (own.kind === "button") {
      o.controls.release(own.control);
      down.delete(own.id);
      els.get(own.id)?.removeAttribute("data-down");
    } else if (own.kind === "stick") {
      o.controls.setVector(o.spec.stick.control, 0, 0);
      if (stick.run && o.spec.stick.run) o.controls.release(o.spec.stick.run);
      stickOwner = null;
      stick = { cx: 0, cy: 0, run: false, x: 0, y: 0, mag: 0 };
      drawStick();
    }
  };
  const releaseAll = (): void => {
    for (const id of [...owners.keys()]) end(id);
  };

  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    o.controls.noteDevice?.("touch"); // any touch on the layer is touch in use (prompts switch at once)
    const target = (e.target as Element | null)?.closest?.("[data-touch-button]") as HTMLElement | null;
    if (target) {
      const b = lay.buttons.find((x) => x.id === target.dataset.touchButton);
      if (!b || isHidden(b) || owners.has(e.pointerId)) return;
      capture(target, e.pointerId);
      owners.set(e.pointerId, { kind: "button", id: b.id, control: b.control });
      down.add(b.id);
      target.dataset.down = "";
      o.controls.press(b.control);
      return;
    }
    const x = e.clientX;
    const y = e.clientY;
    if (stickOwner === null && inRect(x, y, lay.stick.zone)) {
      const R = lay.stick.radius;
      stickOwner = e.pointerId;
      // the ring appears under the thumb, kept on screen
      stick = { cx: Math.max(lay.insets.left + R, x), cy: Math.min(lay.height - lay.insets.bottom - R, y), run: false, x: 0, y: 0, mag: 0 };
      owners.set(e.pointerId, { kind: "stick" });
      capture(root, e.pointerId);
      drawStick();
    } else if (inRect(x, y, lay.look.zone)) {
      owners.set(e.pointerId, { kind: "look", x, y });
      capture(root, e.pointerId);
    }
  };
  const onMove = (e: PointerEvent): void => {
    const own = owners.get(e.pointerId);
    if (!own) return;
    e.preventDefault();
    e.stopPropagation();
    if (own.kind === "stick") {
      const wasRun = stick.run;
      stick = stickMove(stick, e.clientX, e.clientY, lay.stick.radius);
      knob_ = { x: e.clientX - stick.cx, y: e.clientY - stick.cy };
      o.controls.setVector(o.spec.stick.control, stick.x, stick.y);
      if (o.spec.stick.run && stick.run !== wasRun) {
        if (stick.run) o.controls.press(o.spec.stick.run);
        else o.controls.release(o.spec.stick.run);
      }
      drawStick();
    } else if (own.kind === "look") {
      o.controls.addDelta(o.spec.look.control, e.clientX - own.x, e.clientY - own.y);
      own.x = e.clientX;
      own.y = e.clientY;
    }
  };
  const onUp = (e: PointerEvent): void => {
    if (!owners.has(e.pointerId)) return;
    e.preventDefault();
    e.stopPropagation();
    end(e.pointerId);
  };
  const swallow = (e: Event): void => e.stopPropagation();
  root.addEventListener("pointerdown", onDown);
  root.addEventListener("pointermove", onMove);
  root.addEventListener("pointerup", onUp);
  root.addEventListener("pointercancel", onUp);
  root.addEventListener("lostpointercapture", (e) => owners.get(e.pointerId)?.kind === "button" && end(e.pointerId));
  root.addEventListener("contextmenu", (e) => e.preventDefault());
  for (const t of ["click", "mousedown", "mouseup", "touchstart", "touchend"]) root.addEventListener(t, swallow);

  let frame = 0;
  const schedule = (): void => {
    if (frame) return;
    frame = win.requestAnimationFrame(() => {
      frame = 0;
      relayout();
    });
  };
  const hidden = (): void => {
    if (doc.visibilityState === "hidden") releaseAll();
  };
  const turned = (): void => {
    releaseAll();
    schedule();
  };
  win.addEventListener("resize", schedule);
  win.addEventListener("orientationchange", turned);
  win.addEventListener("blur", releaseAll);
  win.addEventListener("pagehide", releaseAll);
  doc.addEventListener("visibilitychange", hidden);
  relayout();

  return {
    element: root,
    get shown() {
      return shown;
    },
    show(on) {
      if (on === shown) return;
      shown = on;
      if (!on) releaseAll();
      root.style.display = on ? "block" : "none";
      if (on) relayout();
    },
    reveal(id, on) {
      if (!els.has(id)) throw new Error(`touch overlay: no button "${id}"`);
      revealed.set(id, !on);
      const b = lay.buttons.find((x) => x.id === id)!;
      if (!on) for (const [pid, own] of owners) if (own.kind === "button" && own.id === id) end(pid);
      if (isHidden(b)) els.get(id)!.dataset.hidden = "";
      else els.get(id)!.removeAttribute("data-hidden");
    },
    layout: () => lay,
    relayout,
    state: () => ({
      shown,
      width: lay.width,
      height: lay.height,
      insets: { ...lay.insets },
      stick: { active: stickOwner !== null, cx: stick.cx, cy: stick.cy, x: stick.x, y: stick.y, mag: stick.mag, run: stick.run, restX: lay.stick.restX, restY: lay.stick.restY, radius: lay.stick.radius },
      look: { active: [...owners.values()].some((x) => x.kind === "look") },
      buttons: lay.buttons.map((b) => ({ ...b, down: down.has(b.id), shown: shown && !isHidden(b) })),
    }),
    releaseAll,
    destroy() {
      releaseAll();
      win.removeEventListener("resize", schedule);
      win.removeEventListener("orientationchange", turned);
      win.removeEventListener("blur", releaseAll);
      win.removeEventListener("pagehide", releaseAll);
      doc.removeEventListener("visibilitychange", hidden);
      if (frame) win.cancelAnimationFrame(frame);
      root.remove();
      probe.remove();
    },
  };
}
